import { SettingUrls } from 'constants/SettingsUrls';
import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useFirstSelectedProvider } from '../../usage-progress-card/hooks/useFirstSelectedProvider';
import { useOrderOverviewTiles } from '../hooks/useOrderOverviewTiles';
import { ActiveProviderLogos } from './ActiveProviderLogos';
import { MostUsedProvider } from './most-used-provider/MostUsedProvider';
import { OrderStatGrid } from './order-stat-grid/OrderStatGrid';

// Dashboard card for a paid or legacy subscription: the order overview on the left and the active
// carriers on the right, in a 2:1 split. A legacy subscription also gets a "Monthly order limit" tile.
export const PlanOverviewDetails: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { currentSubscription, isCancelled, goToPLans } = useCurrentSubscriptionHelper();
    const { providers, activeCount, countryByProvider } = useFirstSelectedProvider();
    const { stats, isLoading } = useOrderOverviewTiles();

    // e.g. "October 02, 2026" — undefined without a `currentPeriodEnd`, which leaves the date out of the banner
    const expiresOn = useMemo(() => {
        if (!currentSubscription?.currentPeriodEnd) return undefined;
        return new Intl.DateTimeFormat(i18n.language, { month: 'long', day: '2-digit', year: 'numeric' }).format(
            new Date(currentSubscription.currentPeriodEnd)
        );
    }, [currentSubscription?.currentPeriodEnd, i18n.language]);

    const showExpiresBanner = isCancelled;

    return (
        <s-section padding="none">
            <div className="grid grid-cols-1 md:grid-cols-3 md:gap-x-2 p-4 md:card-fix-height">
                <div className="flex flex-col gap-3 md:col-span-2 md:pr-4">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <s-text type="strong">
                                    {t('DASHBOARD.PLAN_OVERVIEW_DETAILS.ORDER_OVERVIEW.TITLE')}
                                </s-text>
                                {currentSubscription?.name && <s-badge>{currentSubscription.name}</s-badge>}
                            </div>
                            <s-text color="subdued">
                                {t('DASHBOARD.PLAN_OVERVIEW_DETAILS.ORDER_OVERVIEW.DESCRIPTION')}
                            </s-text>
                        </div>
                        <s-button onClick={() => navigate(SettingUrls.orders)}>
                            {t('DASHBOARD.PLAN_OVERVIEW_DETAILS.ORDER_OVERVIEW.VIEW_ORDERS')}
                        </s-button>
                    </div>

                    <OrderStatGrid isLoading={isLoading} compact={showExpiresBanner} stats={stats} />
                    {showExpiresBanner && (
                        <s-banner tone="info">
                            {expiresOn && (
                                <>
                                    {t('DASHBOARD.PLAN_OVERVIEW_DETAILS.SUBSCRIPTION_EXPIRES.EXPIRES_ON', {
                                        date: expiresOn,
                                    })}{' '}
                                </>
                            )}
                            {t('DASHBOARD.PLAN_OVERVIEW_DETAILS.SUBSCRIPTION_EXPIRES.DESCRIPTION')}{' '}
                            <s-link onClick={goToPLans}>
                                {t('DASHBOARD.PLAN_OVERVIEW_DETAILS.SUBSCRIPTION_EXPIRES.ACTION')}
                            </s-link>
                        </s-banner>
                    )}
                </div>

                <div className="flex flex-col justify-between gap-3 border-t border-border md:pl-4 max-md:mt-3 max-md:pt-3 md:border-t-0 md:border-l">
                    <div className="flex flex-col gap-3">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 flex-col gap-1">
                                <s-text type="strong">{t('DASHBOARD.PLAN_OVERVIEW_DETAILS.CARRIERS.TITLE')}</s-text>
                                <div className="truncate">
                                    <s-text color="subdued">
                                        {t('DASHBOARD.PLAN_OVERVIEW_DETAILS.CARRIERS.ACTIVE', { count: activeCount })}
                                    </s-text>
                                </div>
                            </div>
                            <s-button onClick={() => navigate(SettingUrls.shippingProviders)}>
                                {t('DASHBOARD.PLAN_OVERVIEW_DETAILS.CARRIERS.MANAGE')}
                            </s-button>
                        </div>
                        <ActiveProviderLogos providers={providers} countryByProvider={countryByProvider} />
                    </div>
                    <s-divider />
                    <div className="flex flex-col gap-1">
                        <s-text type="strong">{t('DASHBOARD.PLAN_OVERVIEW_DETAILS.CARRIERS.MOST_USED')}</s-text>
                        <MostUsedProvider />
                    </div>
                </div>
            </div>
        </s-section>
    );
};
